import React, { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import { getStripeUrls } from "../utils/stripe";

const purchasePackages = [
  {
    id: 1,
    quantity: 1,
    price: 1.99,
    originalPrice: 1.99,
    savings: 0,
    popular: false,
    discount: 0,
    perUnit: 1.99,
  },
  {
    id: 2,
    quantity: 50,
    price: 94.99,
    originalPrice: 99.5,
    savings: 4.51,
    popular: true,
    discount: 4.53,
    perUnit: 1.9,
  },
  {
    id: 3,
    quantity: 100,
    price: 182.99,
    originalPrice: 199.0,
    savings: 16.01,
    popular: false,
    discount: 8.05,
    perUnit: 1.83,
  },
  {
    id: 4,
    quantity: 200,
    price: 349.99,
    originalPrice: 398.0,
    savings: 48.01,
    popular: false,
    discount: 12.06,
    perUnit: 1.75,
  },
  {
    id: 5,
    quantity: 500,
    price: 849.99,
    originalPrice: 995.0,
    savings: 145.01,
    popular: false,
    discount: 14.57,
    perUnit: 1.7,
  },
];

const modulePackages = [
  { id: "P500", quantity: 500, stripeKey: "P500" },
  { id: "P1000", quantity: 1000, stripeKey: "P1000" },
  { id: "P1500", quantity: 1500, stripeKey: "P1500" },
  { id: "P2000", quantity: 2000, stripeKey: "P2000" },
  { id: "P2500", quantity: 2500, stripeKey: "P2500" },
  { id: "P3000", quantity: 3000, stripeKey: "P3000" },
  { id: "P3500", quantity: 3500, stripeKey: "P3500" },
  { id: "P4000", quantity: 4000, stripeKey: "P4000" },
  { id: "P4500", quantity: 4500, stripeKey: "P4500" },
  { id: "P5000", quantity: 5000, stripeKey: "P5000" },
  { id: "P5500", quantity: 5500, stripeKey: "P5500" },
  { id: "P6000", quantity: 6000, stripeKey: "P6000" },
  { id: "P6500", quantity: 6500, stripeKey: "P6500" },
  { id: "P7000", quantity: 7000, stripeKey: "P7000" },
  { id: "P7500", quantity: 7500, stripeKey: "P7500" },
  { id: "P8000", quantity: 8000, stripeKey: "P8000" },
  { id: "P8500", quantity: 8500, stripeKey: "P8500" },
  { id: "P9000", quantity: 9000, stripeKey: "P9000" },
  { id: "P9500", quantity: 9500, stripeKey: "P9500" },
  { id: "P10000", quantity: 10000, stripeKey: "P10000" },
];

const PurchaseModal = ({
  show,
  onClose,
  onReturn,
  subscriptionOnly = false,
}) => {
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [resellerTiers, setResellerTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);

  const isModuleReseller =
    user?.type === "Reseller" && user?.resellerType === "module";
  const isRegularReseller =
    user?.type === "Reseller" && user?.resellerType !== "module";
  const shouldShowSubscriptions = user?.type !== "Reseller";
  const [showSubscriptionPricing, setShowSubscriptionPricing] = useState(
    shouldShowSubscriptions
  );

  // Fetch reseller tiers when modal opens for regular resellers
  useEffect(() => {
    const fetchResellerTiers = async () => {
      if (show && isRegularReseller) {
        setLoadingTiers(true);
        try {
          const response = await apiService.getResellerTiers();
          const allTiers = response.data?.tiers || [];

          // Filter tiers based on user's current tier
          // Only show tiers with tier_order <= user's current tier_order
          const userTierOrder = user?.resellerTierOrder || 0;
          const filteredTiers = allTiers
            .filter((tier) => tier.isActive && tier.tierOrder <= userTierOrder)
            .sort((a, b) => a.tierOrder - b.tierOrder);

          setResellerTiers(filteredTiers);
        } catch (error) {
          console.error("Error fetching reseller tiers:", error);
          setResellerTiers([]);
        } finally {
          setLoadingTiers(false);
        }
      }
    };

    fetchResellerTiers();
  }, [show, isRegularReseller, user]);

  useEffect(() => {
    if (show) {
      setShowSubscriptionPricing(shouldShowSubscriptions);
      setSelectedPackage(null);
    }
  }, [show, shouldShowSubscriptions]);

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    const stripeUrls = getStripeUrls(user.id, encodeURIComponent(user.email));

    // Handle module package purchases
    if (selectedPackage.type === "module") {
      const stripeUrl =
        stripeUrls[selectedPackage.stripeKey]?.url ||
        stripeUrls[selectedPackage.stripeKey];

      if (stripeUrl) {
        window.location.href = stripeUrl;
      }

      return;
    }

    // Handle tier purchases
    if (selectedPackage.type === "tier") {
      const tierKey = `tier-${selectedPackage.tierSlug}`;
      const stripeUrl = stripeUrls[tierKey]?.url || stripeUrls[tierKey];

      if (stripeUrl) {
        window.location.href = stripeUrl;
      }

      return;
    }

    // Handle bulk purchases (1-49 reseller option)
    if (selectedPackage.type === "bulk") {
      const stripeUrl =
        stripeUrls[selectedPackage.id]?.url || stripeUrls[selectedPackage.id];

      if (stripeUrl) {
        window.location.href = stripeUrl;
      }

      return;
    }

    // Handle regular and subscription purchases
    const stripeUrl =
      stripeUrls[selectedPackage.quantity || selectedPackage.type]?.url ||
      stripeUrls[selectedPackage.quantity || selectedPackage.type];

    if (stripeUrl) {
      window.location.href = stripeUrl;
    }
  };

  const handleReturn = () => {
    setSelectedPackage(null);
    if (onReturn) {
      onReturn();
    } else {
      onClose();
    }
  };

  const getTierBadgeColor = (tierSlug) => {
    const colors = {
      nano: "from-purple-500 to-purple-600",
      starter: "from-blue-500 to-blue-600",
      basic: "from-cyan-500 to-cyan-600",
      plus: "from-green-500 to-green-600",
      growth: "from-yellow-500 to-yellow-600",
      pro: "from-orange-500 to-orange-600",
      scale: "from-red-500 to-red-600",
      "enterprise-1": "from-pink-500 to-pink-600",
      "enterprise-2": "from-fuchsia-500 to-fuchsia-600",
      "enterprise-3": "from-violet-500 to-violet-600",
      "enterprise-4": "from-indigo-500 to-indigo-600",
    };

    return colors[tierSlug?.toLowerCase()] || "from-gray-500 to-gray-600";
  };

  const renderModulePackages = () => (
    <div className="w-full">
      <div className="max-h-[440px] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60 bg-white dark:bg-gray-800/40">
        {modulePackages.map((pkg) => {
          const selected = selectedPackage?.id === pkg.id;

          return (
            <button
              aria-pressed={selected}
              className={`group relative w-full flex items-center justify-between gap-4 pl-6 pr-5 py-5 text-left transition-colors ${
                selected
                  ? "bg-gray-100 dark:bg-gray-700/40"
                  : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
              }`}
              key={pkg.id}
              type="button"
              onClick={() =>
                setSelectedPackage({
                  id: pkg.id,
                  quantity: pkg.quantity,
                  stripeKey: pkg.stripeKey,
                  type: "module",
                })
              }
            >
              <span
                className={`absolute left-0 top-0 bottom-0 w-1 transition-colors ${
                  selected ? "bg-gray-700 dark:bg-gray-400" : "bg-transparent"
                }`}
              />

              <div className="flex items-baseline gap-4 sm:gap-6 min-w-0">
                <span className="font-mono text-xs sm:text-sm font-medium text-gray-400 dark:text-gray-500 w-16 flex-shrink-0 tracking-wider">
                  {pkg.id}
                </span>
                <span className="text-2xl sm:text-3xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white">
                  {pkg.quantity.toLocaleString()}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
                  codes
                </span>
              </div>

              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  selected
                    ? "border-gray-700 bg-gray-700 dark:border-gray-400 dark:bg-gray-400"
                    : "border-gray-300 dark:border-gray-600 group-hover:border-gray-400 dark:group-hover:border-gray-500"
                }`}
              >
                {selected && <span className="w-2 h-2 bg-white rounded-full" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderRegularResellerPackages = () => (
    <>
      {/* 1-49 Option for Resellers */}
      <div
        className={`relative cursor-pointer rounded-lg p-4 border-2 transition-colors w-[220px] flex-shrink-0 ${
          selectedPackage?.id === "1-49"
            ? "border-[#6495ed] bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-200 dark:border-gray-700 hover:border-[#6495ed]"
        }`}
        onClick={() =>
          setSelectedPackage({
            id: "1-49",
            quantity: 1,
            price: 1.99,
            name: "1-49 Keys",
            type: "bulk",
          })
        }
      >
        <div className="text-center">
          <div className="text-3xl font-bold mb-2">1 - 49</div>
          <div className="text-sm text-gray-500 mb-3">Codes</div>
          <div>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-8">
              $1.99
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              per key
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
              Adjust quantity at checkout
            </div>
          </div>
        </div>
      </div>

      {/* Reseller Tier Packages */}
      {resellerTiers.map((tier) => (
        <div
          className={`relative cursor-pointer rounded-lg p-4 border-2 transition-colors w-[220px] flex-shrink-0 ${
            selectedPackage?.id === tier.id
              ? "border-[#6495ed] bg-blue-50 dark:bg-blue-900/20"
              : "border-gray-200 dark:border-gray-700 hover:border-[#6495ed]"
          }`}
          key={tier.id}
          onClick={() =>
            setSelectedPackage({
              id: tier.id,
              tierId: tier.id,
              tierName: tier.tierName,
              tierSlug: tier.tierSlug,
              quantity: tier.includedKeys,
              price: tier.cost,
              costPerKey: tier.costPerKey,
              discount: tier.discountPercentage,
              type: "tier",
            })
          }
        >
          {tier.discountPercentage > 0 && (
            <div className="absolute -top-3 right-2">
              <span
                className={`text-white text-xs px-2 py-1 rounded-full bg-gradient-to-r ${getTierBadgeColor(tier.tierSlug)}`}
              >
                SAVE {tier.discountPercentage.toFixed(2)}%
              </span>
            </div>
          )}

          <div className="text-center">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${getTierBadgeColor(tier.tierSlug)} mb-3`}
            >
              {tier.tierName}
            </div>
            <div className="text-3xl font-bold mb-2">{tier.includedKeys}</div>
            <div className="text-sm text-gray-500 mb-3">Keys</div>
            <div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                ${tier.cost.toFixed(2)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                ${tier.costPerKey.toFixed(2)} per key
              </div>
              {tier.discountPercentage > 0 && (
                <div className="text-xs text-green-600 dark:text-green-400 font-semibold mt-1">
                  Save ${(1.99 * tier.includedKeys - tier.cost).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );

  const renderSubscriptionPackages = () => (
    <>
      {/* Monthly Subscription */}
      <motion.div
        className={`relative cursor-pointer rounded-lg p-6 border-2 transition-all ${
          selectedPackage?.type === "monthly"
            ? "border-[#6495ed] bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-200 dark:border-gray-700 hover:border-[#6495ed]"
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() =>
          setSelectedPackage({
            type: "monthly",
            price: 1.99,
            name: "Monthly Subscription",
          })
        }
      >
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Monthly</div>
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
            $1.99
          </div>
          <div className="text-sm text-gray-500 mb-4">per month</div>
          <div className="space-y-3 text-left">
            <div className="flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-2 mt-1" />
              <span className="text-sm">1 redeem code that never expires</span>
            </div>
            <div className="flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-2 mt-1" />
              <span className="text-sm">
                Active as long as subscription continues
              </span>
            </div>
            <div className="flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-2 mt-1" />
              <span className="text-sm">
                Cancel anytime via manage subscription
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quarterly Subscription */}
      <motion.div
        className={`relative cursor-pointer rounded-lg p-6 border-2 transition-all ${
          selectedPackage?.type === "quarterly"
            ? "border-[#6495ed] bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-200 dark:border-gray-700 hover:border-[#6495ed]"
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() =>
          setSelectedPackage({
            type: "quarterly",
            price: 5.97,
            name: "Quarterly Subscription",
          })
        }
      >
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Quarterly</div>
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
            $5.97
          </div>
          <div className="text-sm text-gray-500 mb-4">every 3 months</div>
          <div className="space-y-3 text-left">
            <div className="flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-2 mt-1" />
              <span className="text-sm">1 redeem code that never expires</span>
            </div>
            <div className="flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-2 mt-1" />
              <span className="text-sm">
                Active as long as subscription continues
              </span>
            </div>
            <div className="flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-2 mt-1" />
              <span className="text-sm">
                Pay every 3 months for flexibility
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );

  const renderBulkPackages = () =>
    purchasePackages.map((pkg) => (
      <div
        className={`relative cursor-pointer rounded-lg p-4 border-2 transition-colors w-[220px] flex-shrink-0 ${
          selectedPackage?.id === pkg.id
            ? "border-[#6495ed] bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-200 dark:border-gray-700 hover:border-[#6495ed]"
        }`}
        key={pkg.id}
        onClick={() => setSelectedPackage(pkg)}
      >
        {pkg.discount > 0 && (
          <div className="absolute -top-3 right-2">
            <span
              className={`text-white text-xs px-2 py-1 rounded-full ${
                pkg.discount >= 14
                  ? "bg-gradient-to-r from-purple-500 to-purple-600"
                  : pkg.discount >= 12
                    ? "bg-gradient-to-r from-blue-500 to-blue-600"
                    : pkg.discount >= 8
                      ? "bg-gradient-to-r from-green-500 to-green-600"
                      : "bg-gradient-to-r from-gray-500 to-gray-600"
              }`}
            >
              SAVE {pkg.discount.toFixed(2)}%
            </span>
          </div>
        )}

        <div className="text-center">
          <div className="text-3xl font-bold mb-2">
            {pkg.quantity === 1 ? "1 - 49" : pkg.quantity}
          </div>
          <div className="text-sm text-gray-500 mb-3">Codes</div>
          {pkg.discount > 0 ? (
            <div>
              <div className="text-sm line-through text-gray-400">
                ${pkg.originalPrice.toFixed(2)}
              </div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                ${pkg.price.toFixed(2)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                ${pkg.perUnit.toFixed(2)} per key
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 font-semibold">
                Save ${pkg.savings.toFixed(2)}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-8">
                ${pkg.price.toFixed(2)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                per key
              </div>
              {pkg.quantity === 1 && (
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  Adjust quantity at checkout
                </div>
              )}
            </div>
          )}
          {pkg.popular && (
            <div className="mt-3">
              <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs px-3 py-1 rounded-full">
                POPULAR
              </span>
            </div>
          )}
        </div>
      </div>
    ));

  const renderPackageContent = () => {
    if (loadingTiers) {
      return (
        <div className="col-span-full flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-300" />
        </div>
      );
    }

    if (isModuleReseller) {
      return renderModulePackages();
    }

    if (isRegularReseller && resellerTiers.length > 0) {
      return renderRegularResellerPackages();
    }

    if (subscriptionOnly || showSubscriptionPricing) {
      return renderSubscriptionPackages();
    }

    return renderBulkPackages();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center p-4"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          style={{ zIndex: 99999 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleReturn();
            }
          }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className={`relative w-full ${isModuleReseller ? "max-w-2xl" : "max-w-4xl"} max-h-[92vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-[#0b0f1a] shadow-2xl ring-1 ring-gray-200 dark:ring-white/10`}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Decorative gradient glows */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-gray-400/25 via-gray-500/10 to-transparent blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-gradient-to-tr from-gray-500/15 via-gray-400/5 to-transparent blur-3xl"
            />

            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-[0.2em] mb-1.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
                  {isModuleReseller ? "Module Codes" : "LunaKey Packages"}
                </div>
                <h2 className="text-2xl font-bold tracking-tight">
                  {isModuleReseller
                    ? "Purchase Module Codes"
                    : "Purchase Redeem Codes"}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {isModuleReseller
                    ? "Select a code package for your module"
                    : "Select a package that suits your needs"}
                </p>
              </div>
              <button
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-white/10 transition-all flex-shrink-0"
                onClick={handleReturn}
              >
                <i className="fas fa-times text-sm" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="relative px-6 pt-4 pb-5 overflow-y-auto flex-1">
              {/* Package Selection */}
              <div className="flex justify-center">
                {isModuleReseller ? (
                  renderPackageContent()
                ) : (
                  <div className="flex flex-wrap justify-center gap-4 w-full">
                    {renderPackageContent()}
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.02] p-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500/15 text-amber-500 dark:text-amber-400">
                    <i className="fas fa-bolt text-sm" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">
                      Instant Delivery
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                      LunaKeys available immediately
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.02] p-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500/15 text-emerald-500 dark:text-emerald-400">
                    <i className="fas fa-shield-alt text-sm" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">Secure Payment</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                      Stripe safe &amp; secured checkout
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.02] p-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-500/15 text-blue-500 dark:text-blue-400">
                    <i className="fas fa-infinity text-sm" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">Never Expires</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                      Only when redeemed
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="relative px-6 py-4 border-t border-gray-200 dark:border-white/10 bg-white/70 dark:bg-gray-900/60 backdrop-blur-md">
              <div className="flex items-center justify-between gap-3">
                <div className="hidden sm:flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                  <span>We accept</span>
                  <span className="inline-flex gap-2 text-lg text-gray-400 dark:text-gray-500">
                    <i className="fab fa-cc-visa" />
                    <i className="fab fa-cc-mastercard" />
                    <i className="fab fa-cc-amex" />
                    <i className="fab fa-cc-paypal" />
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    className="px-5 py-2.5 rounded-full font-medium text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5 transition-colors"
                    onClick={handleReturn}
                  >
                    Cancel
                  </button>
                  <button
                    className="relative px-6 py-2.5 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 shadow-lg shadow-black/30 dark:from-gray-600 dark:to-gray-800 dark:hover:from-gray-500 dark:hover:to-gray-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-1.5"
                    disabled={!selectedPackage}
                    onClick={handlePurchase}
                  >
                    <i className="fas fa-credit-card text-[11px]" />
                    {subscriptionOnly || showSubscriptionPricing
                      ? "Subscribe Now"
                      : "Purchase Now"}
                    <i className="fas fa-arrow-right text-[11px]" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PurchaseModal;
